// v1 seed for the Review Console overview. The shape is intentionally
// forward-compatible with promotion-svc rows: stage <-> status, title <->
// document_title. v2 can replace this seed with the live promotion-svc
// response without changing component contracts.

export type ReviewStage =
  | "uploaded"
  | "in_review"
  | "approved"
  | "rejected"
  | "promoted";

export type ReviewUrgency = "low" | "normal" | "high";

export type ReviewRoute =
  | "/admin/promotions"
  | "/my-promotions"
  | "/documents";

export type ReviewVisibility = "admin" | "non_admin" | "all";

export interface ReviewItem {
  id: string;
  title: string;
  /** tenant.subjects[].agentId without "-assistant" when possible */
  subject: string;
  stage: ReviewStage;
  urgency: ReviewUrgency;
  /** ISO timestamp */
  updatedAt: string;
  ownerName?: string;
  summary?: string;
  route: ReviewRoute;
  visibleTo: ReviewVisibility;
}

const ROUTE_TO_VISIBILITY: Record<ReviewRoute, ReviewVisibility> = {
  "/admin/promotions": "admin",
  "/my-promotions": "non_admin",
  "/documents": "all",
};

function assertSeedInvariant(items: readonly ReviewItem[]): void {
  for (const item of items) {
    const expected = ROUTE_TO_VISIBILITY[item.route];
    if (item.visibleTo !== expected) {
      throw new Error(
        `reviewSeed invariant violation: item ${item.id} has route ` +
          `${item.route} but visibleTo=${item.visibleTo} (expected ${expected})`,
      );
    }
  }
}

// Seven items cover each stage, each urgency, both audiences, and at
// least three subjects.
export const REVIEW_SEED: readonly ReviewItem[] = [
  {
    id: "rv-001",
    title: "Lab safety procedures - Q3 update",
    subject: "chemistry",
    stage: "in_review",
    urgency: "high",
    updatedAt: "2026-05-18T01:10:00Z",
    ownerName: "Ms. Lin",
    summary:
      "New combustion lab safety checklist plus revised reagent storage rules. Awaiting subject admin review before promotion to canonical curriculum.",
    route: "/admin/promotions",
    visibleTo: "admin",
  },
  {
    id: "rv-002",
    title: "Polynomial factoring worksheet pack",
    subject: "math",
    stage: "uploaded",
    urgency: "normal",
    updatedAt: "2026-05-18T00:42:00Z",
    ownerName: "Mr. Park",
    summary:
      "Twelve graded worksheets covering quadratics through cubic factoring. Auto-extracted from teacher upload; needs subject review.",
    route: "/admin/promotions",
    visibleTo: "admin",
  },
  {
    id: "rv-003",
    title: "Hamlet - close reading guide",
    subject: "english",
    stage: "approved",
    urgency: "normal",
    updatedAt: "2026-05-17T22:15:00Z",
    ownerName: "Ms. Carter",
    summary:
      "Approved by english admin. Waiting for handoff to canonical curriculum library.",
    route: "/admin/promotions",
    visibleTo: "admin",
  },
  {
    id: "rv-004",
    title: "Personal upload: Stoichiometry practice set",
    subject: "chemistry",
    stage: "in_review",
    urgency: "low",
    updatedAt: "2026-05-17T20:00:00Z",
    summary:
      "Your submission is queued behind two earlier requests. No action needed; you'll be notified when review completes.",
    route: "/my-promotions",
    visibleTo: "non_admin",
  },
  {
    id: "rv-005",
    title: "Personal upload: Sonnet meter exercises",
    subject: "english",
    stage: "rejected",
    urgency: "low",
    updatedAt: "2026-05-17T18:30:00Z",
    summary:
      "Returned with reviewer notes: missing answer key and license attribution. Update and resubmit when ready.",
    route: "/my-promotions",
    visibleTo: "non_admin",
  },
  {
    id: "rv-006",
    title: "Calculus midterm review packet",
    subject: "math",
    stage: "promoted",
    urgency: "normal",
    updatedAt: "2026-05-17T14:00:00Z",
    ownerName: "Mr. Park",
    summary:
      "Now live in canonical math curriculum. Available to all math teachers and students from /documents.",
    route: "/documents",
    visibleTo: "all",
  },
  {
    id: "rv-007",
    title: "Lab orientation video transcript",
    subject: "chemistry",
    stage: "promoted",
    urgency: "low",
    updatedAt: "2026-05-16T09:00:00Z",
    summary:
      "Promoted last week. Linked from chemistry-assistant's default reference set.",
    route: "/documents",
    visibleTo: "all",
  },
];

assertSeedInvariant(REVIEW_SEED);
